import { ChangeEvent, useEffect, useState } from "react";
import TextField from "@mui/material/TextField";
import { createExpressAccount, updateExpressAccount } from "../../services/expressMaster";
import { ExpressAccount } from "../../types/express-master.type";
import MasterDialog from "./MasterDialog";
import { MASTER_CODE_MAX_LENGTH, MASTER_NAME_MAX_LENGTH } from "./constants";
import {
  AccountForm,
  AccountFormErrors,
  EMPTY_ACCOUNT_FORM,
  accountFormFrom,
  toAccountRequest,
  validateAccountForm,
} from "./masterForm";

interface AccountDialogProps {
  open: boolean;
  /** The account being edited; null to add one. */
  account: ExpressAccount | null;
  onClose: () => void;
  onSaved: () => void;
}

/** Adds or edits an Express account code (รหัส on the document lines and payment lines). */
export default function AccountDialog({ open, account, onClose, onSaved }: Readonly<AccountDialogProps>) {
  const [form, setForm] = useState<AccountForm>(EMPTY_ACCOUNT_FORM);
  const [errors, setErrors] = useState<AccountFormErrors>({});
  const [saving, setSaving] = useState<boolean>(false);

  useEffect(() => {
    if (!open) return;
    setForm(account ? accountFormFrom(account) : EMPTY_ACCOUNT_FORM);
    setErrors({});
  }, [open, account]);

  const handleCodeChange = (event: ChangeEvent<HTMLInputElement>) =>
    setForm((previous) => ({ ...previous, code: event.target.value.toUpperCase() }));
  const handleNameChange = (event: ChangeEvent<HTMLInputElement>) =>
    setForm((previous) => ({ ...previous, name: event.target.value }));
  const handleActiveChange = (isActive: boolean) => setForm((previous) => ({ ...previous, isActive }));

  const handleSave = async () => {
    const formErrors = validateAccountForm(form);
    setErrors(formErrors);
    if (Object.keys(formErrors).length > 0) return;
    setSaving(true);
    try {
      const body = toAccountRequest(form);
      if (account) await updateExpressAccount(account.uuid, body);
      else await createExpressAccount(body);
      onSaved();
    } catch {
      // The interceptor already shows the server's reason (e.g. a code already used).
    } finally {
      setSaving(false);
    }
  };

  return (
    <MasterDialog
      open={open}
      title={account ? "แก้ไขรหัสบัญชี" : "เพิ่มรหัสบัญชี"}
      saving={saving}
      isActive={form.isActive}
      onActiveChange={handleActiveChange}
      onClose={onClose}
      onSave={handleSave}
    >
      <TextField
        fullWidth
        size="small"
        required
        label="รหัส"
        placeholder="เช่น 5200-58"
        value={form.code}
        onChange={handleCodeChange}
        error={Boolean(errors.code)}
        helperText={errors.code}
        slotProps={{ htmlInput: { maxLength: MASTER_CODE_MAX_LENGTH } }}
      />
      <TextField
        fullWidth
        size="small"
        required
        label="ชื่อบัญชี"
        placeholder="เช่น ค่าเบี้ยเลี้ยงพนักงานขาย"
        value={form.name}
        onChange={handleNameChange}
        error={Boolean(errors.name)}
        helperText={errors.name}
        slotProps={{ htmlInput: { maxLength: MASTER_NAME_MAX_LENGTH } }}
      />
    </MasterDialog>
  );
}
